import { AnnotationContextWindow } from "components/annotations/AnnotationContextWindow";
import { AnnotationPreviewBubble } from "components/annotations/AnnotationPreviewBubble";
import { ReactNode, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { classMerge } from "../utility/classMerge";
import styles from "./AitColumnAnnotationPreviewRow.module.css";
import { AitTablePosition } from "./functions/interface";
import { useTableContext } from "./TableContextProvider";

/**
 * State for the annotation modal used by the column annotation preview row.
 * Holds whether the modal is open and which column/cell it is attached to.
 */
interface AnnotationModalState {
  isOpen: boolean;
  cellId?: string;
  columnIndex?: number;
  originalColumnIndex?: number;
}

/**
 * Render a preview row that displays column annotation bubbles above the header.
 * Only visible when the table is in the ANNOTATIONS view mode.
 */
export const AitColumnAnnotationPreviewRow = <T,>(): ReactNode => {
  const { state } = useTableContext<T>();
  const headerData = state?.processedTableData?.headerData;
  const ref = useRef<HTMLTableRowElement | null>(null);
  const showAnnotationPreview = state?.tlfViewMode.value === "ANNOTATIONS";
  const columnRepeats =
    state?.processedTableData?.columnRepeats ??
    // istanbul ignore next
    [];
  const [annotationModalState, setAnnotationModalState] = useState<AnnotationModalState>({
    isOpen: false,
  });

  if (!showAnnotationPreview || !headerData) {
    return null;
  }

  // Get the last row of the header to access column annotations
  const lastHeaderRow = headerData.rows[headerData.rows.length - 1];
  if (!lastHeaderRow) {
    return null;
  }

  const annotationModal =
    annotationModalState.isOpen &&
    annotationModalState.cellId !== undefined &&
    annotationModalState.columnIndex !== undefined &&
    annotationModalState.originalColumnIndex !== undefined ? (
      <AnnotationContextWindow
        isOpen={annotationModalState.isOpen}
        onClose={() => setAnnotationModalState({ isOpen: false })}
        annotationLocation={{
          position: AitTablePosition.column,
          columnId: annotationModalState.cellId,
          originalColumn: annotationModalState.originalColumnIndex,
        }}
        annotationType="Column"
        contextAnnotation={
          state.originalTableData.columnInformation?.find(
            (ci) => ci.columnId === annotationModalState.cellId,
          )?.annotation
        }
        disabled={!state.editable}
      />
    ) : null;

  return (
    <>
      <tr ref={ref}>
        {/* Left spacer cell for row group column */}
        <td className="ait-cell"></td>

        {/* Column annotation previews */}
        {lastHeaderRow.cells.map((cell, ci) => {
          // Calculate effective colspan (accounts for column repeats)
          const effectiveColSpan = cell.repeatColSpan ?? cell.colSpan;

          // Calculate the width based on effective colspan
          const cellWidth =
            effectiveColSpan >= 2
              ? lastHeaderRow.cells
                  .slice(ci, ci + effectiveColSpan)
                  .map(
                    (c) =>
                      c.colWidth ??
                      // istanbul ignore next
                      state.defaultCellWidth,
                  )
                  .reduce((a, b) => a + b, 0)
              : (cell.colWidth ??
                // istanbul ignore next
                state.defaultCellWidth);

          const displayWidth = `${state.colWidthMod * cellWidth}px`;

          // Get original column index from columnRepeats
          const originalColumnIndex =
            columnRepeats[ci]?.columnIndex ??
            // istanbul ignore next
            ci;

          const isColumnRepeat =
            columnRepeats[ci] !== undefined && columnRepeats[ci].colRepeat !== undefined
              ? columnRepeats[ci].colRepeat.match(/^[[\]0,]+$/) === null
              : false;
          // Get the annotation for this column from columnInformation
          const columnAnnotation = state.originalTableData.columnInformation?.find(
            (colInfo) => colInfo.columnId === cell.columnId,
          )?.annotation;

          return (
            <td
              key={`col-annotation-${cell.columnId}-${ci}`}
              className={classMerge([`ait-cell ${styles.columnAnnotationCell}`])}
              colSpan={effectiveColSpan}
              style={{
                width: displayWidth,
                minWidth: displayWidth,
                maxWidth: displayWidth,
              }}
            >
              {/* Only show annotation preview for non-repeated columns */}
              {!isColumnRepeat && (
                <AnnotationPreviewBubble
                  annotation={columnAnnotation}
                  variant="column"
                  target={ref}
                  onClick={() => {
                    setAnnotationModalState({
                      isOpen: true,
                      cellId: cell.columnId,
                      columnIndex: ci,
                      originalColumnIndex,
                    });
                  }}
                />
              )}
            </td>
          );
        })}
      </tr>
      {/* Render modal outside of thead using a portal */}
      {annotationModal && typeof document !== "undefined"
        ? createPortal(annotationModal, document.body)
        : null}
    </>
  );
};

AitColumnAnnotationPreviewRow.displayName = "AitColumnAnnotationPreviewRow";
